import { ref } from 'vue'
import type { App } from 'vue'
import en from './messages/en'
import zhCN from './messages/zh-CN'

// 定义支持的语言列表
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  'zh-CN': '简体中文',
}

// 语言代码映射到 i18n 的 locale 代码
export const LANGUAGE_CODE_MAP: Record<string, string> = {
  en: 'en',
  'zh-Hans': 'zh-CN',
  'zh-Hant': 'zh-CN',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-CN',
}

// 语言代码映射到 PayPal 的 locale 代码
export const PAYPAL_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  'zh-Hans': 'zh_CN',
  'zh-Hant': 'zh_CN',
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_CN',
}

// 所有语言消息
const messages = {
  en,
  'zh-CN': zhCN,
}

// 获取默认语言
const getDefaultLocale = (): string => {
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language || 'en'
    const mapped = LANGUAGE_CODE_MAP[browserLang]
    if (mapped) return mapped
    if (browserLang.startsWith('zh')) {
      return 'zh-CN'
    }
  }
  return 'en'
}

// 当前语言（全局单例）
// 初始化为 null，等待 store 恢复后再设置，避免刷新时被浏览器语言覆盖
const currentLocale = ref<string>('en')

// 获取嵌套对象的值
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

// 翻译函数 - 每次调用时都读取最新的 locale
function t(key: string, params?: Record<string, any>): string {
  // 每次都从 ref 中读取最新值
  const locale = currentLocale.value
  let message = getNestedValue(messages[locale as keyof typeof messages], key)

  // 如果当前语言没有找到，尝试使用英文
  if (message === undefined) {
    message = getNestedValue(messages.en, key)
  }

  // 如果还是没找到，返回 key
  if (message === undefined) {
    console.warn(`[i18n] Translation key not found: ${key}`)
    return key
  }

  // 替换参数
  if (params && typeof message === 'string') {
    Object.keys(params).forEach((paramKey) => {
      message = message.replace(new RegExp(`{${paramKey}}`, 'g'), params[paramKey])
    })
  }

  return message
}

// 创建 i18n 实例
const i18n = {
  global: {
    locale: currentLocale,
    t,
  },
  install(app: App) {
    // 提供全局属性 $t
    app.config.globalProperties.$t = t

    // 提供 provide/inject
    app.provide('i18n', { locale: currentLocale, t })
  },
}

export default i18n
